// @ts-nocheck
// Reader bookmarks ("המקומות שלי") — saved spots in the book, synced to the
// `bookmarks` table (one row per spot, RLS-scoped to the reader; see
// migration 0022). Unlike useCloudProgress this is a plain CRUD list, not a
// merge-on-load store: bookmarks are explicit user actions (save / delete),
// so there's no debounce and no local/cloud merge — the cloud is the source
// of truth and we re-fetch after each mutation.
//
// Requires an authenticated user. A logged-out reader (incl. the free
// chapter-1 preview before sign-in) gets an empty list and no-op mutators;
// the UI prompts them to log in instead of calling these.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";

export type Bookmark = {
  id: string;
  chapter: number;
  anchor: string | null;
  para_ordinal: number | null;
  section_label: string | null;
  title: string;
  created_at: string;
};

const COLS = "id, chapter, anchor, para_ordinal, section_label, title, created_at";

export type NewBookmark = {
  chapter: number;
  anchor?: string | null;
  paraOrdinal?: number | null;
  sectionLabel?: string | null;
  title?: string | null;
};

export function useBookmarks(user: any) {
  const userId = user?.id || null;
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "saving" | "error">("idle");

  const refresh = useCallback(async () => {
    if (!userId) {
      setBookmarks([]);
      return;
    }
    setStatus("loading");
    const { data, error } = await supabase
      .from("bookmarks")
      .select(COLS)
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) {
      // eslint-disable-next-line no-console
      console.error("bookmarks load failed", error);
      setStatus("error");
      return;
    }
    setBookmarks((data as Bookmark[]) || []);
    setStatus("idle");
  }, [userId]);

  // Reload whenever the signed-in user changes (login / logout / account
  // switch on a shared device).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Save (or update, if the same spot is re-saved) a bookmark. Returns the
  // stored row, or null on failure / no auth.
  const addBookmark = useCallback(
    async (b: NewBookmark): Promise<Bookmark | null> => {
      if (!userId) return null;
      if (!(b.chapter >= 1 && b.chapter <= 12)) return null;
      setStatus("saving");
      const row = {
        user_id: userId,
        chapter: b.chapter,
        anchor: b.anchor || null,
        para_ordinal: typeof b.paraOrdinal === "number" ? b.paraOrdinal : null,
        section_label: b.sectionLabel || null,
        title: (b.title || b.sectionLabel || `פרק ${b.chapter}`).trim().slice(0, 120),
      };
      const { data, error } = await supabase
        .from("bookmarks")
        .upsert(row, { onConflict: "user_id,chapter,anchor" })
        .select(COLS)
        .single();
      if (error) {
        // eslint-disable-next-line no-console
        console.error("bookmark save failed", error);
        setStatus("error");
        return null;
      }
      await refresh();
      return data as Bookmark;
    },
    [userId, refresh],
  );

  const removeBookmark = useCallback(
    async (id: string): Promise<void> => {
      if (!userId) return;
      // Optimistic — drop it from the list immediately, re-fetch on error.
      setBookmarks((prev) => prev.filter((x) => x.id !== id));
      const { error } = await supabase
        .from("bookmarks")
        .delete()
        .eq("id", id)
        .eq("user_id", userId);
      if (error) {
        // eslint-disable-next-line no-console
        console.error("bookmark delete failed", error);
        refresh();
      }
    },
    [userId, refresh],
  );

  return { bookmarks, status, addBookmark, removeBookmark, refresh };
}
